import api from "./axios";

const TEAM_URL = "/teamcriteria";

// GET all criteria for a team in a session
export const getTeamCriterias = (teamId, sessionId) =>
  api.get(`${TEAM_URL}/team/${teamId}/session/${sessionId}`);

/**
 * Unified update for a TeamCriterion
 * @param {number} criterionId
 * @param {number} teamId
 * @param {number} sessionId
 * @param {'increment'|'decrement'|'set_boolean'|'set_text'|'set_text_and_count'|'publish'|'undo_publish'} action
 * @param {boolean|string|null|object} value
 * @param {number} amount
 */
export const updateTeamCriterion = (
  criterionId,
  teamId,
  sessionId,
  action,
  value = null,
  amount = 1
) => {
  const url = `${TEAM_URL}/${criterionId}/team/${teamId}/session/${sessionId}`;
  const params = { action, amount };

  if (action === "set_boolean" || action === "set_text") {
    return api.put(url, { value }, { params });
  } else if (action === "set_text_and_count") {
    return api.put(url, value, { params });
  } else {
    return api.put(url, {}, { params });
  }
};

// Convenience wrappers
export const incrementTeamCriterion = (criterionId, teamId, sessionId) =>
  updateTeamCriterion(criterionId, teamId, sessionId, "increment");

export const decrementTeamCriterion = (criterionId, teamId, sessionId) =>
  updateTeamCriterion(criterionId, teamId, sessionId, "decrement");

export const setTeamBooleanValue = (criterionId, teamId, sessionId, value) =>
  updateTeamCriterion(criterionId, teamId, sessionId, "set_boolean", value);

export const setTeamTextValue = (criterionId, teamId, sessionId, value) =>
  updateTeamCriterion(criterionId, teamId, sessionId, "set_text", value);

export const setTeamCountValue = (criterionId, teamId, sessionId, value) =>
  updateTeamCriterion(criterionId, teamId, sessionId, "set_count", value);

export const setTeamTextAndCountValue = (
  criterionId,
  teamId,
  sessionId,
  text = undefined,
  count = undefined
) => {
  const payload = {};
  if (text !== undefined) payload.text = text;
  if (count !== undefined) payload.count = count;

  return updateTeamCriterion(
    criterionId,
    teamId,
    sessionId,
    "set_text_and_count",
    payload
  );
};

export const publishTeamCriterion = (criterionId, teamId, sessionId) =>
  updateTeamCriterion(criterionId, teamId, sessionId, "publish");

export const undoPublishTeamCriterion = (criterionId, teamId, sessionId) =>
  updateTeamCriterion(criterionId, teamId, sessionId, "undo_publish");

export const getTeamHistory = (sessionId) =>
  api.get(`${TEAM_URL}/history/team/session/${sessionId}`);

export const setTeamPublishState = (historyId, publish) =>
  api.put(`${TEAM_URL}/history/${historyId}/publish`, null, {
    params: { publish }
  });

/**
 * Delete a specific TeamCriterion history entry
 * @param {number} historyId
 */
export const deleteTeamHistoryEntry = (historyId) =>
  api.delete(`${TEAM_URL}/history/${historyId}`);

/**
 * Swap a team criterion history entry to a user criterion history entry
 * @param {number} historyId
 * @param {number} userId
 * @param {number|null} changeValue - The change value to apply to the user's current value
 */
export const swapTeamHistoryToUser = (historyId, userId, changeValue = null) => {
  const params = changeValue !== null ? { change_value: changeValue } : {};
  return api.post(`${TEAM_URL}/history/${historyId}/swap-to-user/${userId}`, null, { params });
};

/**
 * Swap a team criterion history entry to another team
 * @param {number} historyId
 * @param {number} targetTeamId
 * @param {number|null} changeValue - The change value to apply to the target team's current value
 */
export const swapTeamHistoryToTeam = (historyId, targetTeamId, changeValue = null) => {
  const params = changeValue !== null ? { change_value: changeValue } : {};
  return api.post(`${TEAM_URL}/history/${historyId}/swap-to-team/${targetTeamId}`, null, { params });
};

/**
 * Update a team criterion history entry
 * @param {number} historyId
 * @param {object} data - { int_value, bool_value, text_value }
 */
export const updateTeamHistoryEntry = (historyId, data) =>
  api.put(`${TEAM_URL}/history/${historyId}`, data);